import { useState } from 'react';
import { DatePicker, Flex, Typography } from 'antd';
import dayjs from 'dayjs';
import { useLazyExportWriteOffsXlsxQuery, useWriteOffsReportQuery } from '@/entities/reports';
import type { WriteOffsReportRow } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import type { ResponsiveColumns } from '@/shared/ui';
import { useBreakpoint } from '@/shared/lib/hooks';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz, formatDate } from '@/shared/lib/date';

const REASON_LABELS: Record<string, string> = {
  SPOILAGE: 'Порча',
  EXPIRED: 'Истёк срок',
  SHORTAGE: 'Недостача',
  DEFECT: 'Брак',
  OTHER: 'Другое',
};

/** §13, §11.4: списания по причинам и товарам, включая недостачу инвентаризации. */
export function WriteOffsReportPage() {
  const ctx = useStoreContext();
  const { isMobile } = useBreakpoint();
  const today = todayInTz(ctx.timezone);
  const defaultFrom = dayjs(today).startOf('month').format('YYYY-MM-DD');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(today);
  const periodChanged = from !== defaultFrom || to !== today;
  const { data, isFetching } = useWriteOffsReportQuery({ from, to }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportWriteOffsXlsxQuery();

  const columns: ResponsiveColumns<WriteOffsReportRow> = [
    { title: 'Причина', dataIndex: 'reason', width: 160, mobile: 'badge', render: (v: string) => REASON_LABELS[v] ?? v },
    { title: 'Товар', dataIndex: 'categoryName', mobile: 'title' },
    { title: 'Документов', dataIndex: 'linesCount', width: 110, align: 'right' },
    { title: 'Штук', dataIndex: 'qty', width: 100, align: 'right' },
    { title: 'Сумма', dataIndex: 'costAmount', width: 120, align: 'right' },
  ];

  return (
    <ReportPage
      title="Списания"
      filters={
        <DatePicker.RangePicker
          format="DD.MM.YYYY"
          inputReadOnly={isMobile}
          value={[dayjs(from), dayjs(to)]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setFrom(dates[0].format('YYYY-MM-DD'));
              setTo(dates[1].format('YYYY-MM-DD'));
            }
          }}
        />
      }
      summary={
        data ? (
          <Flex justify="flex-end" className="no-print">
            <Typography.Text strong>Итого: {data.totalCostAmount}</Typography.Text>
          </Flex>
        ) : null
      }
      columns={columns}
      activeFilters={periodChanged ? 1 : 0}
      onResetFilters={() => {
        setFrom(defaultFrom);
        setTo(today);
      }}
      filtersSummary={`${formatDate(from)} — ${formatDate(to)}`}
      data={data?.rows ?? []}
      loading={isFetching}
      rowKey={(r) => `${r.reason}-${r.categoryId}`}
      onExport={() => exportXlsx({ from, to }).unwrap()}
      exportFilename="Списания.xlsx"
    />
  );
}
